/**
 * Quand un virement cite deux familles à la fois.
 *
 * ## Le cas, et ce qu'il coûterait
 *
 * Virement du 25 mars 2026, 79,00 €, communication :
 *
 *     NC-25-00589 - CDV-MC-2600396  REMBOURSEMENT ANNULATION
 *
 * Les deux références sont valides, et elles ne désignent pas la même famille.
 * `CDV-MC-2600396` est la facture de **Beatriz Camargo Magalhaes** — annulée,
 * créditée par `NC-26-00041`, payée 79,00 € qui dorment depuis en provision.
 * `NC-25-00589` est une note de 69,00 € d'**Andrei Arnaut**, sur une tout autre
 * facture. Le numéro de note a été recopié de travers.
 *
 * L'écran, lui, lit les deux et propose la note : un clic placerait 69,00 € sur
 * la note d'Andrei Arnaut — qui n'a rien demandé —, laisserait 10,00 € à placer,
 * et la provision de Beatriz resterait à 79,00 €. Trois erreurs d'un seul geste,
 * dont aucune ne lèverait d'invariant : la note vaut bien 69,00 €, le virement
 * vaut bien 79,00 €, et la base n'a aucune raison de refuser.
 *
 * ## Un seul cas, et c'est pour cela qu'il est dangereux
 *
 * Mesuré le 20 septembre 2026 sur les 3 525 virements sortants : **un seul**
 * cite des pièces de deux familles différentes, et c'est celui-ci — encore en
 * file, à placer aujourd'hui. Un défaut qui se produit une fois sur trois mille
 * ne s'attrape pas à l'œil : rien n'alerte, et l'habitude dit de cliquer.
 *
 * ## Ce module ne bloque pas
 *
 * Il DIT le désaccord. Les deux familles peuvent être légitimes — un parent qui
 * règle pour un autre, une organisation qui paie pour plusieurs. C'est
 * l'administration qui tranche, mais elle doit voir avant de trancher.
 */

/** Une pièce citée par un virement, et la famille qu'elle désigne. */
export interface PieceCitee {
  userId: string | null;
  famille: string | null;
  /** Le numéro, tel qu'il figure dans la communication. */
  piece: string;
}

export interface FamilleCitee {
  userId: string;
  famille: string;
  pieces: string[];
}

export interface FamillesCitees {
  /** Les familles atteintes par les notes de crédit citées. */
  parLesNotes: FamilleCitee[];
  /** Les familles atteintes par les factures citées. */
  parLesFactures: FamilleCitee[];
  /**
   * Vrai quand les deux lectures ne se recoupent NULLE PART, chacune désignant
   * au moins une famille. Un recoupement partiel n'est pas un désaccord : une
   * facture peut citer une note d'une autre famille tout en citant aussi la
   * sienne, et l'écran n'a alors rien à redire.
   */
  enDesaccord: boolean;
  /** Le désaccord en une phrase, ou `null` s'il n'y en a pas. */
  phrase: string | null;
}

export function lireLesFamillesCitees(
  notes: PieceCitee[],
  factures: PieceCitee[],
): FamillesCitees {
  const parLesNotes = regrouper(notes);
  const parLesFactures = regrouper(factures);

  const idsDesNotes = new Set(parLesNotes.map((f) => f.userId));
  const communes = parLesFactures.filter((f) => idsDesNotes.has(f.userId));

  const enDesaccord = parLesNotes.length > 0
    && parLesFactures.length > 0
    && communes.length === 0;

  return {
    parLesNotes,
    parLesFactures,
    enDesaccord,
    phrase: enDesaccord ? phrase(parLesNotes, parLesFactures) : null,
  };
}

function regrouper(pieces: PieceCitee[]): FamilleCitee[] {
  const par = new Map<string, FamilleCitee>();
  for (const p of pieces) {
    if (!p.userId) continue;
    const deja = par.get(p.userId);
    if (deja) {
      if (!deja.pieces.includes(p.piece)) deja.pieces.push(p.piece);
    } else {
      par.set(p.userId, {
        userId: p.userId,
        famille: p.famille ?? 'Compte sans nom',
        pieces: [p.piece],
      });
    }
  }
  return [...par.values()];
}

function phrase(notes: FamilleCitee[], factures: FamilleCitee[]): string {
  const dire = (fs: FamilleCitee[]) => fs
    .map((f) => `${f.famille} (${f.pieces.join(', ')})`)
    .join(' et ');

  return `Ce virement cite des pièces de deux familles différentes : `
    + `${dire(notes)} par la note de crédit, ${dire(factures)} par la facture. `
    + `Un numéro a probablement été recopié de travers — placer sur la note `
    + `créditerait une famille qui n'a rien demandé.`;
}
